import { createContext, useContext, useEffect, useState } from "react";
import { type Session, type User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "master_admin" | "staff" | "partner" | "a";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: AppRole | null;
  isAdmin: boolean;
  isStaff: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRole = async (userId: string, email?: string) => {
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .single();
      if (data?.role) {
        setRole(data.role as AppRole);
      } else {
        // Fallback: owner email always gets master_admin
        setRole(email === "islamzaky.sharpvision@gmail.com" || email === "zaky@izverse.store" ? "master_admin" : null);
      }
    } catch {
      setRole(email === "islamzaky.sharpvision@gmail.com" || email === "zaky@izverse.store" ? "master_admin" : null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchRole(session.user.id, session.user.email).finally(() => setIsLoading(false));
      else setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchRole(session.user.id, session.user.email);
      else { setRole(null); setIsLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => { await supabase.auth.signOut(); };

  const isAdmin = role === "master_admin" || role === "a";
  const isStaff = role === "staff";

  return (
    <AuthContext.Provider value={{ session, user, role, isAdmin, isStaff, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}

